import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';

import {
  ButtonComponent,
  IconComponent,
  Maybe,
  Menu,
  MenuComponent,
  MenuTriggerStateDirective,
  OptionalAsyncPipe,
  ResponsiveService,
  ThemeSelectorComponent,
  ThemeService,
  ThemeType,
  ValueOf,
  without,
} from '@evc/web-components';

import { AuthService } from '../../../clients/core/auth/service/auth.service';
import { LanguageService } from '../../i18n/language.service';
import { PlatformTranslatePipe } from '../../i18n/platform-translate.pipe';
import { ThemePreferenceService } from '../../theme/theme-preference.service';
import { Guid } from '../../types/api-objects.type';
import { UserMenuComponent } from '../menus/user/user-menu';
import {
  NAVIGATIONS_MENUS,
  NAVIGATIONS_MODES,
  type NavigationsMode,
  TERTIARY_NAVIGATION_HOSTS,
  type TertiaryNavigationHosts,
} from '../types';

export const TERTIARY_NAVIGATION_MENUS = without(NAVIGATIONS_MENUS, 'APPS');
export type TertiaryNavigationMenus = ValueOf<typeof TERTIARY_NAVIGATION_MENUS>;

/** usefull to auto bind burger-menu props */
export type TertiaryNavigationProps = {
  settings?: Menu;
  help?: Menu;
  /** you may hide the user menu */
  user: boolean;
  layout: NavigationsMode;
};

@Component({
  selector: 'evc-navigation-tertiary',
  imports: [
    UserMenuComponent,
    IconComponent,
    MenuComponent,
    ButtonComponent,
    MenuTriggerStateDirective,
    OptionalAsyncPipe,
    PlatformTranslatePipe,
    OptionalAsyncPipe,
    ThemeSelectorComponent,
  ],
  templateUrl: './navigation-tertiary.html',
  styleUrls: ['./navigation-tertiary.scss', './navigation-tertiary-ims.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-host]': 'host()',
    '[attr.data-mode]': 'mode()',
    '[attr.data-theme]': "isIms() ? 'ims' : null",
    '[attr.data-pending]': 'isPending()',
  },
})
export class NavigationTertiaryComponent {
  readonly login = input<Maybe<() => void>>(undefined);
  readonly logout = input<Maybe<() => void>>(undefined);
  readonly changeOrganization = input<Maybe<(organizationId: Guid) => void>>(undefined);

  readonly settings = input<Menu>();
  readonly displayThemeSelector = input(true);
  readonly help = input<Menu>();
  readonly langs = input<Menu>();
  readonly isIms = input(false);
  readonly user = input<boolean>(true);
  readonly mode = input<NavigationsMode>(NAVIGATIONS_MODES.PRIVATE);
  readonly host = input<TertiaryNavigationHosts>(TERTIARY_NAVIGATION_HOSTS.TOPBAR);

  readonly #languageService = inject(LanguageService);
  readonly #authService = inject(AuthService);
  readonly #themePreferenceService = inject(ThemePreferenceService);
  readonly #themeService = inject(ThemeService);

  protected readonly lang: Signal<Maybe<string>> = this.#languageService.lang;
  protected readonly currentTheme = this.#themeService.theme;
  protected readonly isPending = this.#authService.shouldPromptOrganizationSelection;

  protected readonly HOSTS = TERTIARY_NAVIGATION_HOSTS;
  protected readonly MENUS = TERTIARY_NAVIGATION_MENUS;
  protected readonly MODES = NAVIGATIONS_MODES;
  protected readonly responsive = inject(ResponsiveService);
  protected readonly isImsDesktop = computed(() => this.responsive.isDesktop() && this.isIms());

  protected readonly buttonFrame = computed((): 'full' | 'ghost' =>
    this.isImsDesktop() ? 'full' : 'ghost',
  );
  protected readonly buttonType = computed((): 'primary' | 'neutral' =>
    this.isImsDesktop() ? 'primary' : 'neutral',
  );
  protected saveThemePreference(theme: ThemeType) {
    if (theme === 'ims') return;
    this.#themePreferenceService.selectTheme(theme);
  }
}
